/** Membership loss is surfaced once on Home; the unresolved offer stays in Settings. */
import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { cachedFamily, clearFamilyView, activeMembers, nextFamilyOwner } from "@/lib/family";
import { familyDb } from "@/lib/db";
import { getRemovedFamilyRows, resolveRemovedFamilyRows } from "@/lib/session";
import { familyError } from "./use_family";
import { resolveEmptyRemoval } from "@/lib/sync";
import { haptics } from "@/lib/haptics";

const surfacedFamilies = new Set<string>();
export function useFamilyAccountSummary() {
  const family = useLiveQuery(cachedFamily, [], null);
  const me = family ? activeMembers(family).find((member) => member.member_id === family.my_member_id) : undefined;
  return {
    family,
    hasOthers: !!family && activeMembers(family).some((member) => member.member_id !== family.my_member_id),
    owner: me?.role === "owner",
    nextOwner: family ? nextFamilyOwner(family) : undefined,
  };
}
export function useFamilyRemoval(surface = false) {
  const removed = useLiveQuery(getRemovedFamilyRows, [], null);
  const previousFamily = useLiveQuery(cachedFamily, [], null);
  const categories = useLiveQuery(() => familyDb.categories.toArray(), [], []);
  const profile = useLiveQuery(() => familyDb.profile.toCollection().first(), []);
  const [surfaced, setSurfaced] = useState<string | null>(null);
  useEffect(() => {
    if (surface && removed?.rows.length && !surfacedFamilies.has(removed.familyId)) {
      surfacedFamilies.add(removed.familyId);
      setSurfaced(removed.familyId);
    }
  }, [surface, removed]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!removed || removed.rows.length) return;
    void resolveEmptyRemoval(removed.familyId).catch(cause => setError(familyError(cause)));
  }, [removed]);
  const resolve = async (choice: "copy" | "discard") => {
    if (busy) return false;
    setBusy(true);
    setError(null);
    try {
      const result = await resolveRemovedFamilyRows(choice);
      await clearFamilyView();
      setNotice(
        `${result.copied} ${result.copied === 1 ? "expense" : "expenses"} copied. ${
          Object.entries(result.discarded)
            .map(([table, count]) => `${count} ${table}`)
            .join(", ") || "No other changes"
        } discarded.`
      );
      haptics("success");
      return true;
    } catch (cause) {
      setError(familyError(cause));
      haptics("error");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return {
    removed,
    previousFamily,
    categories,
    profile,
    busy,
    error,
    notice,
    resolve,
    show: !!removed && surfaced === removed.familyId,
    dismiss: () => setSurfaced(null),
  };
}
export type FamilyRemovalState = ReturnType<typeof useFamilyRemoval>;
